// Browser-compatible Cloudinary upload service
// Uses direct HTTP requests to Cloudinary's REST API

import { getCloudinaryConfig, validateCloudinaryConfig as validateConfig } from './cloudinary-config-parser';

// Get Cloudinary configuration (supports both CLOUDINARY_URL and individual variables)
let cloudinaryConfig: ReturnType<typeof getCloudinaryConfig> | null = null;

function getConfig() {
  if (!cloudinaryConfig) {
    try {
      cloudinaryConfig = getCloudinaryConfig();
    } catch (error) {
      console.error('❌ Cloudinary configuration error:', error);
      // Fallback to individual environment variables for backward compatibility
      cloudinaryConfig = {
        cloudName: import.meta.env.VITE_CLOUDINARY_CLOUD_NAME || 'your_cloud_name',
        apiKey: import.meta.env.VITE_CLOUDINARY_API_KEY || 'your_api_key',
        uploadPreset: import.meta.env.VITE_CLOUDINARY_UPLOAD_PRESET || 'your_upload_preset'
      };
    }
  }
  return cloudinaryConfig;
}

// Lazy-loaded configuration
const getCloudName = () => getConfig().cloudName;
const getApiKey = () => getConfig().apiKey;
const getUploadPreset = () => getConfig().uploadPreset;

// Configuration constants
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_FORMATS = ['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp'];
const MAX_RETRY_ATTEMPTS = 3;
const RETRY_DELAY = 1000; // 1 second

// Types for the upload response
export interface CloudinaryUploadResult {
  supplierName: string;
  imageUrl: string;
  uploadStatus: string;
  publicId?: string;
  secureUrl?: string;
  width?: number;
  height?: number;
  format?: string;
  bytes?: number;
  error?: string;
  folderPath?: string;
  tags?: string[];
  uploadedAt?: string;
}

export interface SupplierInfo {
  id: string;
  name: string;
  email?: string;
}

export interface ProductInfo {
  id?: string;
  name: string;
  slug?: string;
}

export interface UploadOptions {
  maxRetries?: number;
  retryDelay?: number;
  transformation?: string[];
  // Note: quality and format options are configured in the upload preset for unsigned uploads
}

/**
 * Validates file before upload
 */
export function validateFile(file: File): { isValid: boolean; error?: string } {
  // Check file size
  if (file.size > MAX_FILE_SIZE) {
    return {
      isValid: false,
      error: `File size (${(file.size / 1024 / 1024).toFixed(2)}MB) exceeds maximum allowed size (${MAX_FILE_SIZE / 1024 / 1024}MB)`
    };
  }

  // Check file format
  const fileExtension = file.name.split('.').pop()?.toLowerCase();
  if (!fileExtension || !ALLOWED_FORMATS.includes(fileExtension)) {
    return {
      isValid: false,
      error: `File format '${fileExtension}' is not supported. Allowed formats: ${ALLOWED_FORMATS.join(', ')}`
    };
  }

  // Check if file is actually an image
  if (!file.type.startsWith('image/')) {
    return {
      isValid: false,
      error: 'File must be an image'
    };
  }

  return { isValid: true };
}

/**
 * Formats supplier name to create a valid folder path
 * Handles special characters and spaces appropriately
 */
export function formatSupplierFolderName(supplierName: string): string {
  return supplierName
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '') // Remove special characters except spaces
    .replace(/\s+/g, '_') // Replace spaces with underscores
    .replace(/_{2,}/g, '_') // Replace multiple underscores with single
    .replace(/^_+|_+$/g, '') // Remove leading/trailing underscores
    .substring(0, 50); // Limit length to 50 characters
}

/**
 * Creates a unique public ID for the image
 */
export function generatePublicId(fileName: string, supplierName: string): string {
  const timestamp = Date.now();
  const cleanFileName = fileName
    .split('.')[0] // Remove extension
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '_')
    .substring(0, 30);
  
  return `${timestamp}_${cleanFileName}`;
}

/**
 * Sleep function for retry delays
 */
const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Client-side image upload using Cloudinary's unsigned upload with retry mechanism
 */
export async function uploadImageToCloudinary(
  file: File,
  supplier: SupplierInfo,
  product?: ProductInfo,
  options: UploadOptions = {}
): Promise<CloudinaryUploadResult> {
  const {
    maxRetries = MAX_RETRY_ATTEMPTS,
    retryDelay = RETRY_DELAY
  } = options;

  // Validate file first
  const validation = validateFile(file);
  if (!validation.isValid) {
    return {
      supplierName: formatSupplierFolderName(supplier.name),
      imageUrl: '',
      uploadStatus: `Failed: ${validation.error}`,
      error: validation.error,
      uploadedAt: new Date().toISOString()
    };
  }

  let lastError: Error | null = null;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      console.log(`📤 Upload attempt ${attempt}/${maxRetries} for file: ${file.name}`);

      // Format supplier name for folder
      const supplierFolderName = formatSupplierFolderName(supplier.name);
      
      // Create folder structure: suppliers/{supplierName}/{productSlug}
      let folderPath = `suppliers/${supplierFolderName}`;
      if (product?.slug) {
        folderPath += `/${product.slug}`;
      }
      
      // Generate unique public ID
      const publicId = generatePublicId(file.name, supplierFolderName);
      
      // Create form data for upload
      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_preset', getUploadPreset() || '');
      formData.append('folder', folderPath);
      formData.append('public_id', publicId);
      // Note: quality and format parameters are not allowed for unsigned uploads
      
      // Add tags for better organization
      const tags = [
        'product_image',
        `supplier_${supplier.id}`,
        supplierFolderName,
        'auto_uploaded'
      ];
      if (product?.id) {
        tags.push(`product_${product.id}`);
      }
      formData.append('tags', tags.join(','));
      
      // Add context for better searchability
      const context = {
        supplier_name: supplier.name,
        supplier_id: supplier.id,
        ...(product?.name && { product_name: product.name }),
        ...(product?.id && { product_id: product.id }),
        upload_source: 'product_form'
      };
      formData.append('context', Object.entries(context).map(([k, v]) => `${k}=${v}`).join('|'));
      
      // Upload to Cloudinary with timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
      
      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${getCloudName()}/image/upload`,
        {
          method: 'POST',
          body: formData,
          signal: controller.signal
        }
      );
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Upload failed: ${response.status} ${response.statusText} - ${errorText}`);
      }
      
      const result = await response.json();
      
      // Verify the upload was successful
      if (!result.secure_url || !result.public_id) {
        throw new Error('Upload response missing required fields');
      }
      
      console.log(`✅ Successfully uploaded ${file.name} on attempt ${attempt}`);
      
      // Return formatted response
      return {
        supplierName: supplierFolderName,
        imageUrl: result.secure_url,
        uploadStatus: 'Success',
        publicId: result.public_id,
        secureUrl: result.secure_url,
        width: result.width,
        height: result.height,
        format: result.format,
        bytes: result.bytes,
        folderPath,
        tags,
        uploadedAt: new Date().toISOString()
      };
      
    } catch (error) {
      lastError = error instanceof Error ? error : new Error('Unknown error');
      console.warn(`❌ Upload attempt ${attempt}/${maxRetries} failed for ${file.name}:`, lastError.message);
      
      // If this is the last attempt, don't wait
      if (attempt < maxRetries) {
        console.log(`⏳ Waiting ${retryDelay}ms before retry...`);
        await sleep(retryDelay);
      }
    }
  }
  
  // All attempts failed
  console.error(`❌ All ${maxRetries} upload attempts failed for ${file.name}`);
  
  return {
    supplierName: formatSupplierFolderName(supplier.name),
    imageUrl: '',
    uploadStatus: `Failed: ${lastError?.message || 'Unknown error'}`,
    error: lastError?.message || 'Unknown error',
    uploadedAt: new Date().toISOString()
  };
}

/**
 * Upload multiple images for a product with parallel processing
 */
export async function uploadProductImages(
  files: File[],
  supplier: SupplierInfo,
  product?: ProductInfo,
  options: UploadOptions = {}
): Promise<CloudinaryUploadResult[]> {
  console.log(`📤 Starting batch upload of ${files.length} images`);
  
  // Validate all files first
  const validationResults = files.map(file => ({ file, ...validateFile(file) }));
  const invalidFiles = validationResults.filter(result => !result.isValid);
  
  if (invalidFiles.length > 0) {
    console.warn(`⚠️ ${invalidFiles.length} files failed validation:`, invalidFiles);
  }
  
  // Upload valid files in parallel
  const validFiles = validationResults.filter(result => result.isValid).map(result => result.file);
  const uploadPromises = validFiles.map(file => 
    uploadImageToCloudinary(file, supplier, product, options)
  );
  
  // Add results for invalid files
  const invalidResults: CloudinaryUploadResult[] = invalidFiles.map(({ file, error }) => ({
    supplierName: formatSupplierFolderName(supplier.name),
    imageUrl: '',
    uploadStatus: `Failed: ${error}`,
    error,
    uploadedAt: new Date().toISOString()
  }));
  
  const uploadResults = await Promise.all(uploadPromises);
  const allResults = [...uploadResults, ...invalidResults];
  
  const successCount = allResults.filter(result => result.uploadStatus === 'Success').length;
  console.log(`✅ Batch upload completed: ${successCount}/${files.length} successful`);
  
  return allResults;
}

/**
 * Upload images with progress tracking and better error handling
 */
export async function uploadImagesWithProgress(
  files: File[],
  supplier: SupplierInfo,
  product?: ProductInfo,
  onProgress?: (progress: number, completed: number, total: number, currentFile?: string) => void,
  options: UploadOptions = {}
): Promise<CloudinaryUploadResult[]> {
  const results: CloudinaryUploadResult[] = [];
  const total = files.length;
  
  console.log(`📤 Starting sequential upload with progress tracking: ${total} files`);
  
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    
    try {
      // Notify progress with current file
      if (onProgress) {
        const progress = (i / total) * 100;
        onProgress(progress, i, total, file.name);
      }
      
      const result = await uploadImageToCloudinary(file, supplier, product, options);
      results.push(result);
      
      const completed = i + 1;
      const progress = (completed / total) * 100;
      
      if (onProgress) {
        onProgress(progress, completed, total);
      }
      
      console.log(`📊 Progress: ${completed}/${total} (${progress.toFixed(1)}%)`);
      
    } catch (error) {
      console.error(`❌ Failed to upload file ${file.name}:`, error);
      results.push({
        supplierName: formatSupplierFolderName(supplier.name),
        imageUrl: '',
        uploadStatus: `Failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        error: error instanceof Error ? error.message : 'Unknown error',
        uploadedAt: new Date().toISOString()
      });
    }
  }
  
  const successCount = results.filter(result => result.uploadStatus === 'Success').length;
  console.log(`✅ Sequential upload completed: ${successCount}/${total} successful`);
  
  return results;
}

/**
 * Browser-compatible image verification using Cloudinary's REST API
 */
export async function verifyImageUpload(publicId: string): Promise<boolean> {
  try {
    const response = await fetch(
      `https://res.cloudinary.com/${getCloudName()}/image/upload/${publicId}.json`,
      { method: 'HEAD' } // Use HEAD to just check existence
    );
    return response.ok;
  } catch (error) {
    console.error('Image verification error:', error);
    return false;
  }
}

/**
 * Verify multiple images exist
 */
export async function verifyMultipleImages(publicIds: string[]): Promise<{ [publicId: string]: boolean }> {
  const verificationPromises = publicIds.map(async (publicId) => {
    const exists = await verifyImageUpload(publicId);
    return { publicId, exists };
  });
  
  const results = await Promise.all(verificationPromises);
  return results.reduce((acc, { publicId, exists }) => {
    acc[publicId] = exists;
    return acc;
  }, {} as { [publicId: string]: boolean });
}

/**
 * Get optimized image URL with transformations
 */
export function getOptimizedImageUrl(
  publicId: string,
  options: {
    width?: number;
    height?: number;
    quality?: string;
    format?: string;
    crop?: string;
    gravity?: string;
    effect?: string;
  } = {}
): string {
  const baseUrl = `https://res.cloudinary.com/${getCloudName()}/image/upload`;
  
  const transformations: string[] = [];
  
  if (options.width || options.height) {
    const dimensions = [
      options.width && `w_${options.width}`,
      options.height && `h_${options.height}`,
      options.crop && `c_${options.crop}`,
      options.gravity && `g_${options.gravity}`
    ].filter(Boolean).join(',');
    
    if (dimensions) {
      transformations.push(dimensions);
    }
  }
  
  if (options.quality) {
    transformations.push(`q_${options.quality}`);
  }
  
  if (options.format) {
    transformations.push(`f_${options.format}`);
  }
  
  if (options.effect) {
    transformations.push(`e_${options.effect}`);
  }
  
  const transformationString = transformations.length > 0 
    ? `/${transformations.join('/')}`
    : '';
  
  return `${baseUrl}${transformationString}/${publicId}`;
}

/**
 * Get supplier folder structure preview
 */
export function getSupplierFolderStructure(supplier: SupplierInfo, product?: ProductInfo): {
  supplierFolder: string;
  fullPath: string;
  exampleUrl: string;
} {
  const supplierFolder = formatSupplierFolderName(supplier.name);
  let fullPath = `suppliers/${supplierFolder}`;
  
  if (product?.slug) {
    fullPath += `/${product.slug}`;
  }
  
  const exampleUrl = `https://res.cloudinary.com/${getCloudName()}/image/upload/${fullPath}/example_image.jpg`;
  
  return {
    supplierFolder,
    fullPath,
    exampleUrl
  };
}

/**
 * Delete image using Cloudinary's REST API (requires signed request)
 * Note: This requires server-side implementation for security
 */
export async function deleteImage(publicId: string): Promise<boolean> {
  console.warn('Delete operation requires server-side implementation for security');
  // This would need to be implemented on your backend
  // as it requires API secret for signed requests
  return false;
}

/**
 * Get supplier images (requires server-side implementation)
 */
export async function getSupplierImages(supplierName: string): Promise<any[]> {
  console.warn('Get supplier images requires server-side implementation');
  // This would need to be implemented on your backend
  // as it requires API secret for admin operations
  return [];
}

/**
 * Configuration validation
 */
export function validateCloudinaryConfig(): { isValid: boolean; errors: string[] } {
  return validateConfig();
} 